import _ from 'lodash';
import { getSecondCBILSAccount } from '../../api/CBILSSelector';
import { hasOpenApplication } from './multiLoanDashboardSelector';
import { differenceInDays } from 'date-fns';
import { getCountry } from '../../iwIntlProvider/CountryProvider';
import { hasCreatedFlexiLoanApplicationInAccountPages } from '../applyFlexiLoanView/applyFlexiLoanSelectors';
import {
    hasApplicationWithProductType,
    getApplicationDuration,
    getApplicationStatus,
    APPLICATION_STATUS_DECLINED
} from '../../api/lendingAPI/ApplicationSelector';
import { isFlexi24Duration } from '../flexi24/getIsFlexi24Duration';
import { FLEXI_24_URL } from '../flexi24/dashboard.saga';
import { TERM_LOAN_URL } from '../termLoan/Dashboard.sagas';
import { RLS_PRODUCT_TYPE } from '../RLS/dashboard/dashboardSelectors';
import { RBL_URL } from '../revenueBasedLoan/dashboard/Dashboard.sagas';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { RootState } from '@iwProvider/configureStore';
import {
    GetAccountsResponse,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import { TProductType } from '../dashboard/Dashboard.types';
import { FormalOffer } from '../myOffers/MyOffers.types';
import { MIN_DRAWDOWN_AMOUNT } from '../minDrawdownAmount';

export const OUTSTANDING_STATUS = 'ongoing';
export const OVERDUE_STATUS = 'overdue';
export const AWAITING_FUNDS_STATUS = 'pending';
export const TOPUP_STATUS = 'topup';
export const ENDED_STATUS = 'ended';
export const UNKNOWN_STATUS = 'unknown'; // this is either Approved not taken, approval expired, declined or deferred (see https://iwoca.atlassian.net/browse/CBIL-151)
export const APPROVED_STATUS = 'approved';
export const APPROVAL_EXPIRED_STATUS = 'approvalExpired';
export const APPLY_FLEXI_LOAN_STATUS = 'applyFlexiLoan';
export const PENDING_FLEXI_LOAN_STATUS = 'pendingFlexiLoan';
export const DECLINED_STATUS = 'declined';
export const REVOKED_STATUS = 'offer_revoked';
export const DEFERRED_STATUS = 'deferred';

type TFormalOffers = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'];
type TFormalOffer = TFormalOffers[number];
type TAccount = NonNullable<GetAccountsResponse['data']>['accounts'][number];
type TDuration = {
    amount: number;
    unit: 'days' | 'months' | 'years';
};

export const getDashboardStatus = (
    state: RootState,
    productType: TProductType
) => {
    const latestFormalOffer = getFormalOfferOfTypeFromAccounts(
        state,
        productType
    );

    const approvalExpired = hasApprovalExpired(state, productType);
    const isValidOffer = hasValidOffer(state, productType);

    const hasOutstandingPrincipal =
        getOutstandingPrincipal(state, productType) > 0.1;

    const loanStatus = getAccountLoanStatus(state, productType);

    if (
        loanStatus === undefined &&
        !hasCreatedFlexiLoanApplicationInAccountPages(state) &&
        !hasSuggestedFormaloffer(state, productType) &&
        hasOpenApplication(state, productType)
    ) {
        return PENDING_FLEXI_LOAN_STATUS;
    }

    if (
        (!hasOutstandingPrincipal || loanStatus === ENDED_STATUS) &&
        (!latestFormalOffer ||
            (latestFormalOffer && loanStatus === ENDED_STATUS)) &&
        (hasDeclinedCreditFacilityApplicationSelector(state, productType) ||
            hasRevokedCreditFacilityApplicationSelector(state, productType))
    ) {
        return DECLINED_STATUS;
    }
    if (
        (!hasOutstandingPrincipal || loanStatus === ENDED_STATUS) &&
        latestFormalOffer &&
        !hasRevokedCreditFacilityApplicationSelector(state, productType) &&
        !approvalExpired &&
        isValidOffer
    ) {
        return APPROVED_STATUS;
    }

    if (
        approvalExpired &&
        isValidOffer &&
        !loanStatus &&
        (!hasOpenApplication(state, productType) ||
            hasCreatedFlexiLoanApplicationInAccountPages(state)) &&
        !hasDeferredCreditFacilityApplicationSelector(state, productType)
    ) {
        return APPROVAL_EXPIRED_STATUS;
    }
    if (
        latestFormalOffer &&
        (!isValidOffer ||
            hasRevokedCreditFacilityApplicationSelector(state, productType)) &&
        !approvalExpired &&
        loanStatus !== OUTSTANDING_STATUS &&
        loanStatus !== OVERDUE_STATUS
    ) {
        return REVOKED_STATUS;
    }

    if (
        [OVERDUE_STATUS, AWAITING_FUNDS_STATUS, TOPUP_STATUS].includes(
            loanStatus
        )
    ) {
        return loanStatus;
    }

    if (loanStatus !== ENDED_STATUS && hasOutstandingPrincipal) {
        return OUTSTANDING_STATUS;
    }

    if (
        loanStatus === ENDED_STATUS &&
        (!hasOpenApplication(state, productType) ||
            hasCreatedFlexiLoanApplicationInAccountPages(state))
    ) {
        return ENDED_STATUS;
    }

    if (hasDeferredCreditFacilityApplicationSelector(state, productType)) {
        return DEFERRED_STATUS;
    }

    if (!hasOpenApplication(state, productType) && getCountry() === 'UK') {
        return APPLY_FLEXI_LOAN_STATUS;
    }

    if (hasOpenApplication(state, productType)) {
        return PENDING_FLEXI_LOAN_STATUS;
    }

    return UNKNOWN_STATUS;
};

const hasSuggestedFormaloffer = (
    state: RootState,
    productType: TProductType
) => {
    const formalOffer = getFormalOfferOfTypeFromAccounts(state, productType);
    return formalOffer?.is_suggested;
};
const getFormalOfferOfTypeFromAccounts = (
    state: RootState,
    productType: TProductType
) => {
    const accounts = state?.api?.accounts?.data?.accounts;
    const formalOfferfromAccount = _.find(accounts, (account) =>
        _.find(
            account.formal_offers,
            (formal_offer) => formal_offer?.product_type === productType
        )
    );

    if (productType === FLEXI_24_PRODUCT_TYPE && !formalOfferfromAccount) {
        const flexi24formalOfferfromAccount = _.find(accounts, (account) =>
            _.find(
                account.formal_offers,
                (formal_offer) =>
                    formal_offer?.product_type === FLEXI_LOAN_PRODUCT_TYPE &&
                    isFlexi24Duration(formal_offer?.max_duration)
            )
        );
        return flexi24formalOfferfromAccount;
    }
    return formalOfferfromAccount;
};

export const getProductTypeFromOfferId = (
    { formal_offers }: { formal_offers: TFormalOffers },
    offerId: string
) => {
    const formalOffer = formal_offers.find(
        (offer) => offer.offer_id === offerId
    );
    return formalOffer!.product_type;
};

function hasRevokedCreditFacilityApplicationSelector(
    state: RootState,
    productType: TProductType
) {
    const hasRevokedApplication =
        state?.api?.application?.data?.status === REVOKED_STATUS;

    return (
        hasApplicationWithProductType(state, productType) &&
        hasRevokedApplication
    );
}

function hasDeclinedCreditFacilityApplicationSelector(
    state: RootState,
    productType: TProductType = FLEXI_LOAN_PRODUCT_TYPE
) {
    const hasDeclinedApplication =
        state?.api?.application?.data?.status === DECLINED_STATUS;

    return (
        hasApplicationWithProductType(state, productType) &&
        hasDeclinedApplication
    );
}

function hasDeferredCreditFacilityApplicationSelector(
    state: RootState,
    productType: TProductType
) {
    const hasDeferredApplication =
        state?.api?.application?.data?.status === DEFERRED_STATUS;

    return (
        hasApplicationWithProductType(state, productType) &&
        hasDeferredApplication
    );
}

function getBalance(state: RootState, productType?: TProductType) {
    const accountWithProductType = getAccountByProductType(state, productType);
    return accountWithProductType?.balance;
}

export function getOutstandingTotal(
    state: RootState,
    productType?: TProductType
) {
    return getBalance(state, productType)?.total;
}

export function getAccountId(state: RootState, productType: TProductType) {
    return getAccountByProductType(state, productType)?.account_id;
}

export function getDrawdownOfferId(
    state: RootState,
    productType: TProductType
) {
    //drawdown can be a second drawdown on an offer that's still valid or a topup drawdown on a new offer
    const account = getAccountByProductType(state, productType);
    const formalOffers = account?.formal_offers ?? [];
    //topups are currently only one offer, so if there are more than one formal offers, we assume it's a drawdown on the current offer agreement
    if (formalOffers.length >= 2) {
        return account?.loan_offer_id ?? null;
    } else {
        return account?.latest_formal_offer?.offer_id ?? null;
    }
}

export function getDrawdownCreditLimit(
    state: RootState,
    productType: TProductType
) {
    const account = getAccountByProductType(state, productType);
    const offer_id =
        account.formal_offers.length >= 2
            ? account.loan_offer_id
            : account.latest_formal_offer.offer_id;
    return account.formal_offers.find(
        (offer: TFormalOffer) => offer.offer_id === offer_id
    )?.max_amount;
}

export function getOutstandingPrincipal(
    state: RootState,
    productType?: TProductType
) {
    return getBalance(state, productType)?.principal;
}

export function getOutstandingInterest(
    state: RootState,
    productType: TProductType
) {
    return getBalance(state, productType)?.interest;
}

export const getTimeOutDate = (state: RootState, productType: TProductType) => {
    const formalOfferTimeOut = getLatestFormalOfferOfTypeFromAccounts(
        state,
        productType
    )?.timeout;
    if (formalOfferTimeOut) {
        return new Date(formalOfferTimeOut).toISOString();
    }
};

const getAccountLoanStatus = (state: RootState, productType: TProductType) => {
    return getAccountByProductType(state, productType)?.loan_status;
};

export function hasApprovalExpired(
    state: RootState,
    productType: TProductType
) {
    const timeOutDate = getTimeOutDate(state, productType);
    return differenceInDays(new Date(timeOutDate!), new Date()) < 0;
}

function hasValidOffer(state: RootState, productType: TProductType) {
    return (
        getLatestFormalOfferOfTypeFromAccounts(state, productType)?.is_valid ??
        true
    );
}

export function getOtherFees(state: RootState, productType?: TProductType) {
    return getBalance(state, productType)?.other_fees;
}

export function getOutstandingFees(
    state: RootState,
    productType: TProductType
) {
    return (
        getBalance(state, productType)?.other_fees +
        getBalance(state, productType)?.product_fees
    );
}

export function getNextRepaymentAmount(
    state: RootState,
    productType: TProductType
) {
    return getRepaymentsSchedule(state, productType)?.[0]?.total;
}

export function getNextRepaymentDate(
    state: RootState,
    productType: TProductType
) {
    return getRepaymentsSchedule(state, productType)?.[0]?.date;
}

function getRepaymentsSchedule(state: RootState, productType: TProductType) {
    return getScheduledPayments(state, productType)?.schedule;
}

export function getMaxMonthlyInterestRate(
    state: RootState,
    productType: TProductType
) {
    const rates =
        getScheduledPayments(state, productType)?.summary?.rate_structures?.[0]
            ?.rates ?? [];
    const maximumPrincipleRate = _.maxBy(rates, 'max_principal');
    // @ts-expect-error This is likely a bug
    return maximumPrincipleRate?.rate!;
}

export function getScheduledPayments(
    state: RootState,
    productType: TProductType
) {
    const accountOfProductType = getAccountByProductType(state, productType);
    return accountOfProductType?.scheduled_payments;
}

export const FLEXI_LOAN_PRODUCT_TYPE = 'credit_facility';
export const FLEXI_24_PRODUCT_TYPE = 'flexi24';
export const REVENUE_BASED_LOAN_PRODUCT_TYPE = 'merchant_cash_advance';
export const BUSINESS_LOAN_PRODUCT_TYPE = 'term_loan';
export const IWOCAPAY_PRODUCT_TYPE = 'iwocapay';
export const CBILS_PRODUCT_TYPE = 'cbils';
function getNonCBILSAccountProductType(state: RootState) {
    return getAccountByProductType(state)?.product_type;
}

export function getAccountByProductType(
    state: RootState,
    productType?: TProductType
) {
    if (!productType)
        return _.find(
            getAllAccounts(state),
            ({ product_type }) => product_type !== CBILS_PRODUCT_TYPE
        );

    const account = _.find(
        getAllAccounts(state),
        ({ product_type }) => product_type === productType
    );
    if (productType === FLEXI_24_PRODUCT_TYPE && !account) {
        return _.find(
            getAllAccounts(state),
            (account) =>
                account.product_type === FLEXI_LOAN_PRODUCT_TYPE &&
                isFlexi24Duration(account?.duration)
        );
    }

    return account;
}

export function isPrimaryAccountCBILS(state: RootState) {
    const primaryAccountProductType =
        state?.api?.accounts?.data?.accounts?.[0]?.product_type;
    return primaryAccountProductType === CBILS_PRODUCT_TYPE;
}

export function getCreditLimit(state: RootState, productType?: TProductType) {
    const accountWithProductType = getAccountByProductType(state, productType);
    return accountWithProductType?.max_approved_loan_amount;
}

export function getAllAccounts(state: RootState) {
    return state?.api?.accounts?.data?.accounts ?? [];
}

function getTopUpEligibility(state: RootState, productType?: TProductType) {
    return getAccountByProductType(state, productType)?.topup_eligibility;
}

export function isTopUpEligible(state: RootState, productType: TProductType) {
    const isTopupAllowed =
        getTopUpEligibility(state, productType)?.is_allowed ?? false;
    if (productType) {
        return isTopupAllowed;
    }

    return (
        getNonCBILSAccountProductType(state) !== FLEXI_24_PRODUCT_TYPE &&
        isTopupAllowed
    );
}

export function getMaxOutstandingPrincipalBeforeTopUpAllowed(
    state: RootState,
    productType: TProductType
) {
    return (
        getTopUpEligibility(state, productType)
            ?.max_outstanding_principal_before_topup ?? false
    );
}

export function amountToRepayBeforeTopUp(
    state: RootState,
    productType?: TProductType
) {
    return getTopUpEligibility(state, productType)
        ?.amount_to_repay_before_topup;
}

export function getLatestFormalOfferFromAccounts(state: RootState) {
    return getAccountWithLatestFormalOffer(state)?.latest_formal_offer;
}

export function getLatestFormalOfferProductType(state: RootState) {
    const latestFormalOffer = getLatestFormalOfferFromAccounts(state);
    const formalOfferProductType = latestFormalOffer?.product_type;

    return formalOfferProductType;
}

const TWELVE_MONTHS_INTEREST_FREE_PROMOTION = 'twelve_months_interest_free';
export function getInterestRateFromCBILSAccount(cbilsAccount: TAccount) {
    const interestRates = cbilsAccount?.interest_rates;
    const hasTwelveMonthsInterestFree = !!_.find(
        cbilsAccount?.promotions ?? [],
        (promotion) => promotion === TWELVE_MONTHS_INTEREST_FREE_PROMOTION
    );
    const today = new Date();
    const loanAgreementDate = new Date(interestRates?.[0]?.from_date!);
    const timeoutOfTwelveMonthInterestFree = loanAgreementDate.setFullYear(
        loanAgreementDate.getFullYear() + 1
    );
    let activeRate;

    if (
        hasTwelveMonthsInterestFree &&
        // @ts-expect-error
        today < timeoutOfTwelveMonthInterestFree
    ) {
        activeRate = _.find(
            interestRates,
            ({ from_date }) =>
                new Date(from_date!).setHours(23, 59, 59, 999) >
                timeoutOfTwelveMonthInterestFree
        );
    } else {
        activeRate = _.findLast(
            interestRates,
            // @ts-expect-error
            ({ from_date }) => today > new Date(from_date).setHours(0, 0, 0, 0)
        );
    }

    return activeRate?.rate;
}

export function getSecondCBILSInterestRate(state: RootState) {
    const secondCBILSAccount = getSecondCBILSAccount(state)!;
    return getInterestRateFromCBILSAccount(secondCBILSAccount);
}

export function hasLatestFormalOffer(
    state: RootState,
    productType: TProductType
) {
    const latestFormalOffer = getLatestFormalOfferFromAccounts(state);
    if (!latestFormalOffer) return false;

    const latestFormalOfferProductType = latestFormalOffer?.product_type;

    if (productType) {
        return latestFormalOfferProductType === productType;
    }

    return latestFormalOfferProductType !== CBILS_PRODUCT_TYPE;
}

export function getMaxCashAmount(state: RootState, productType: TProductType) {
    return (
        getLatestFormalOfferOfTypeFromAccounts(state, productType)
            ?.available_cash ?? 0
    );
}

export function getMaxDrawDownAmountToTwoDecimalPlaces(
    state: RootState,
    productType: TProductType
) {
    productType = DEProductTypeConversion(productType);
    const availableCash = getMaxCashAmount(state, productType);

    // This rounds the number down to two decimal places
    return Math.floor((availableCash + Number.EPSILON) * 100) / 100;
}

export function getLatestFormalOfferMonthlyInterestRate(
    state: RootState,
    productType: TProductType
) {
    const offer_id = getDrawdownOfferId(state, productType);
    const account = getAccountByProductType(state, productType);
    return account.formal_offers.find(
        (offer: TFormalOffer) => offer.offer_id === offer_id
    )?.interest_rates[0].rate;
}

const hasExpiredApproval = (state: RootState, productType: TProductType) => {
    const latestFormalOffer = getLatestFormalOfferOfTypeFromAccounts(
        state,
        productType
    );
    const latestFormalOfferWithCredit = latestFormalOffer?.available_credit > 0;

    const timeoutDate = latestFormalOffer?.timeout;

    if (!timeoutDate && latestFormalOfferWithCredit) {
        return true;
    }

    return differenceInDays(new Date(timeoutDate), new Date()) < 0;
};

const getAccountWithLatestFormalOffer = (state: RootState) => {
    const accounts = state?.api?.accounts?.data?.accounts ?? [];
    const latestFormalOfferAccount = _.find(
        accounts,
        ({ latest_formal_offer }) => latest_formal_offer?.available_credit > 0
    );

    return latestFormalOfferAccount;
};

const getCashflows = (state: RootState, productType: TProductType) => {
    const accountWithProductType = getAccountByProductType(state, productType);
    return accountWithProductType?.cashflows;
};

export const getLatestFundingCashflowDate = (
    state: RootState,
    productType: TProductType
) => {
    const cashflows = getCashflows(state, productType);
    const latestFunding = _.findLast(cashflows, {
        direction: 'iwoca_to_customer'
    });
    return latestFunding ? latestFunding.datetime : null;
};

export function getRevenueStreamTagsFromAccounts(state: RootState) {
    return getRevenueBasedLoanDetails(state)?.repayment_revenue_stream_tags;
}

function getLatestFormalOfferOfTypeFromAccounts(
    state: RootState,
    productType: TProductType
) {
    const accounts = state?.api?.accounts?.data?.accounts;
    const accountWithOffer = _.find(
        accounts,
        (account) => account?.latest_formal_offer?.product_type === productType
    );

    if (productType === FLEXI_24_PRODUCT_TYPE && !accountWithOffer) {
        const flexi24Offer = _.find(
            accounts,
            (account) =>
                account?.latest_formal_offer?.product_type ===
                    FLEXI_LOAN_PRODUCT_TYPE &&
                isFlexi24Duration(account?.latest_formal_offer?.max_duration)
        );
        return flexi24Offer?.latest_formal_offer;
    }

    return accountWithOffer?.latest_formal_offer;
}

function getRevenueBasedLoanDetails(state: RootState) {
    return getAccountByProductType(state)?.revenue_based_loan_details ?? false;
}

export function getRevenueSweepFraction(state: RootState) {
    return getRevenueBasedLoanDetails(state)?.revenue_sweep_fraction;
}

export function getRevenueNextRepaymentDate(state: RootState) {
    return getRevenueBasedLoanDetails(state)?.next_repayment_date;
}

export function getDrawnDownLoanAmount(
    state: RootState,
    productType?: TProductType
) {
    return getAccountByProductType(state, productType)?.loan_amount;
}

export function getRevenueFeeAmountAtLoanCreation(state: RootState) {
    return getRevenueBasedLoanDetails(state)?.loan_fee;
}

export function getRevenueEstimatedTimeToRepayInMonths(state: RootState) {
    return getRevenueBasedLoanDetails(state)?.estimated_time_to_repay_in_months;
}

export function hasLoanBeenNewlyFundedWithoutRepayments(
    state: RootState,
    productType: TProductType
) {
    const revenueLoanAmountAtLoanCreation = getDrawnDownLoanAmount(
        state,
        productType
    );
    const creditLimit = getCreditLimit(state, productType);
    const outstandingPrincipal = getOutstandingPrincipal(state, productType);

    if (revenueLoanAmountAtLoanCreation === outstandingPrincipal) {
        if (creditLimit > revenueLoanAmountAtLoanCreation) {
            return true;
        }
    }

    return false;
}

function getLatestOfferStatus(state: RootState) {
    const latestFormalOffer = getLatestFormalOfferFromAccounts(state);

    return latestFormalOffer?.status;
}

export function hasLoanBeenFundedWithRepaymentsAndExpiredApproval(
    state: RootState
) {
    const revenueLoanAmountAtLoanCreation = getDrawnDownLoanAmount(state);
    const creditLimit = getCreditLimit(state);
    const outstandingPrincipal = getOutstandingPrincipal(state);
    const latestFormalOfferStatus = getLatestOfferStatus(state);

    if (outstandingPrincipal < revenueLoanAmountAtLoanCreation) {
        if (creditLimit !== revenueLoanAmountAtLoanCreation) {
            if (latestFormalOfferStatus === 'expired') {
                return true;
            }
        }
    }

    return false;
}

export function getMaxAmountCreditAvailable(
    state: RootState,
    productType: TProductType
) {
    const creditLimit = getCreditLimit(state, productType);
    const expired = hasExpiredApproval(state, productType);
    const latestFormalOffer = getLatestFormalOfferOfTypeFromAccounts(
        state,
        productType
    );

    if (!expired && latestFormalOffer) {
        return latestFormalOffer.max_amount;
    }
    return creditLimit;
}

export function getAccountByAccountId(state: RootState, accountId: string) {
    return getAllAccounts(state).find(
        (account: TAccount) => account.account_id === accountId
    );
}

export const getProductTypeFromURL = () => {
    const pathName = getCurrentLocation().pathname;

    if (_.includes(pathName, FLEXI_24_URL)) {
        return FLEXI_24_PRODUCT_TYPE;
    }

    if (_.includes(pathName, TERM_LOAN_URL)) {
        return BUSINESS_LOAN_PRODUCT_TYPE;
    }

    if (_.includes(pathName, '/recovery-loan-scheme/')) {
        return RLS_PRODUCT_TYPE;
    }

    if (_.includes(pathName, RBL_URL)) {
        return REVENUE_BASED_LOAN_PRODUCT_TYPE;
    }

    return FLEXI_LOAN_PRODUCT_TYPE;
};

function hasFlexi24(state: RootState) {
    return (
        hasApplicationWithProductType(state, FLEXI_24_PRODUCT_TYPE) ||
        Boolean(
            getLatestFormalOfferOfTypeFromAccounts(state, FLEXI_24_PRODUCT_TYPE)
        ) ||
        Boolean(getAccountByProductType(state, FLEXI_24_PRODUCT_TYPE))
    );
}

function hasFlexi12(state: RootState) {
    const latestFormalOffer = getLatestFormalOfferOfTypeFromAccounts(
        state,
        FLEXI_LOAN_PRODUCT_TYPE
    );

    const accountFromProductType = getAccountByProductType(
        state,
        FLEXI_LOAN_PRODUCT_TYPE
    );
    return (
        (hasApplicationWithProductType(state, FLEXI_LOAN_PRODUCT_TYPE) &&
            isFlexi12Duration(getApplicationDuration(state))) ||
        Boolean(
            latestFormalOffer &&
                isFlexi12Duration(latestFormalOffer.max_duration)
        ) ||
        Boolean(
            accountFromProductType &&
                isFlexi12Duration(accountFromProductType.duration)
        )
    );
}

export function isLongerLoan(state: RootState) {
    return !hasFlexi12(state) && !hasFlexi24(state);
}

export const hasOtherValidAccounts = (
    state: RootState,
    productType: TProductType
) => {
    const accounts = getAllAccounts(state);

    if (accounts.length > 0) {
        const filteredAccounts = accounts.filter(
            ({ product_type }: { product_type: string }) =>
                product_type !== productType
        );
        if (filteredAccounts.length > 0) {
            return true;
        }
        return false;
    }

    return false;
};

function isFlexi12Duration(duration: TDuration) {
    if (duration) {
        return (
            (duration.amount === 365 && duration.unit === 'days') ||
            (duration.amount === 12 && duration.unit === 'months')
        );
    }
}

export function getRevenueBasedLoanActiveRepaymentIDs(state: RootState) {
    return getRevenueBasedLoanDetails(state)?.active_repayment_bank_accounts;
}

export const getLoanDurationByMonth = (
    state: RootState,
    productType: TProductType = FLEXI_LOAN_PRODUCT_TYPE
) => {
    //if loan is not outstanding we use account with latestformalOffer
    const loanStatus = getAccountLoanStatus(state, productType);
    let maxDuration;
    if (loanStatus === OUTSTANDING_STATUS) {
        maxDuration =
            getAccountByProductType(state, productType)?.duration ?? 0;
    } else
        maxDuration =
            getLatestFormalOfferOfTypeFromAccounts(state, productType)
                ?.max_duration ?? 0;

    if (maxDuration.unit === 'days') {
        return getMonthsfromDays(maxDuration.amount);
    }
    return maxDuration.amount;
};

export function getMonthsfromDays(days: number) {
    return Math.floor(days / 30);
}

export const isTopUpPending = (state: RootState, productType: TProductType) => {
    return (
        hasOpenApplication(state, productType) &&
        !hasCreatedFlexiLoanApplicationInAccountPages(state)
    );
};

export const DEProductTypeConversion = (productType: TProductType) => {
    if (getCountry() !== 'DE') return productType;
    if (productType === FLEXI_24_PRODUCT_TYPE) {
        return FLEXI_LOAN_PRODUCT_TYPE;
    }
    return productType;
};

// We check for > MIN_DRAWDOWN_AMOUNT here for when there is residual available cash which isn't able to be drawn down
// E.g. they have less than £1 or £2 (which was stopping topup applications)
// We chose MIN_DRAWDOWN_AMOUNT as a safeguard to cover all customers since they cant drawn down less than that anyway.
export const hasAvailableCash = (state: RootState, productType: TProductType) =>
    getMaxCashAmount(state, productType) >= MIN_DRAWDOWN_AMOUNT;

const canTopUpNow = (state: RootState, productType: TProductType) =>
    isTopUpEligible(state, productType) &&
    amountToRepayBeforeTopUp(state) === 0;

export const canTopUpAfterRepayments = (
    state: RootState,
    productType: TProductType
) =>
    !isLongerLoan(state) &&
    !canTopUpNow(state, productType) &&
    isTopUpEligible(state, productType) &&
    !hasAvailableCash(state, productType);

export const canRequestTopUp = (state: RootState, productType: TProductType) =>
    canTopUpNow(state, productType) &&
    !isTopUpPending(state, productType) &&
    (hasApprovalExpired(state, productType) ||
        !hasAvailableCash(state, productType));

export const isTopUpApproved = (
    state: RootState,
    productType: TProductType
) => {
    productType = DEProductTypeConversion(productType);
    const isInterestOnly = isInterestOnlyLoan(state, productType);

    return (
        isLatestFormalOfferValid(state, productType) &&
        !hasApprovalExpired(state, productType) &&
        (hasAvailableCash(state, productType) || isInterestOnly)
    );
};

export const hasOldOfferThatsStillValid = (
    state: RootState,
    productType: TProductType
) => {
    const account = getAccountByProductType(state, productType);

    const currentOffer = account.loan_offer_id;

    const hasUsedOffer = account.formal_offers.find(
        (offer: FormalOffer) => offer.offer_id === currentOffer
    );

    return isTopUpApproved(state, productType) && hasUsedOffer;
};

const isLatestFormalOfferValid = (
    state: RootState,
    productType: TProductType
) => {
    return getLatestFormalOfferOfTypeFromAccounts(state, productType)?.is_valid;
};

export const isTopUpDeclined = (state: RootState) =>
    getApplicationStatus(state) === APPLICATION_STATUS_DECLINED;

export const isPreApproved = (state: RootState, productType: TProductType) => {
    const latestFormalOffer = getLatestFormalOfferFromAccounts(state);

    return (
        latestFormalOffer?.is_pre_approval &&
        isTopUpEligible(state, productType) &&
        isTopUpApproved(state, productType)
    );
};

export const isInterestOnlyLoan = (
    state: RootState,
    productType: TProductType
) => {
    return getLatestFormalOfferOfTypeFromAccounts(state, productType)
        ?.interest_only_repayments;
};

export const isInterestOnlyLoanWithNoAvailableCash = (
    state: RootState,
    productType: TProductType
) => {
    const isInterestOnly = isInterestOnlyLoan(state, productType);
    return isInterestOnly && !hasAvailableCash(state, productType);
};

export const isInterestOnlyLoanWithAvailableCash = (
    state: RootState,
    productType: TProductType
) => {
    const isInterestOnly = isInterestOnlyLoan(state, productType);
    return isInterestOnly && hasAvailableCash(state, productType);
};
